// EMobility/EMobilityPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  Button,
  keyframes,
  styled,
  Modal,
  IconButton,
} from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";

// Animation keyframes
const floatToCorner = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translate(-65vw, -40vh) rotate(0deg) scale(0.8);
    opacity: 1;
  }
`;

const slideFromTop = keyframes`
  0% {
    transform: translateY(-100px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideFromLeft = keyframes`
  0% {
    transform: translateX(-100px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const scaleUp = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const slide = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-100%); }
`;

// Styled Components
const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "60vh",
  background: "linear-gradient(135deg, #000 0%, #1627e3ff 50%, #000 100%)",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: "auto",
  padding: "10px",

  // Media queries for different resolutions
  [theme.breakpoints.up(1920)]: {
    // 1K resolution
    padding: "25px",
  },
  [theme.breakpoints.up(2560)]: {
    // 2K resolution
    padding: "30px",
  },
  [theme.breakpoints.up(3840)]: {
    // 4K resolution
    padding: "40px",
  },
}));

const AnimatedButton = styled(Box)(({ theme, isanimating, isvisible }) => ({
  position: "fixed",
  top: "50%",
  right: "27%",
  transform: "translateY(-50%)",
  zIndex: 1000,
  animation:
    isanimating === "true"
      ? `${floatToCorner} 1.5s ease-in-out forwards`
      : "none",
  opacity: isvisible === "true" ? 1 : 0,
  transition: "opacity 0.3s ease",

  // Responsive positioning
  [theme.breakpoints.up(1920)]: {
    // 1K
    top: "55%",
    right: "29.5%",
  },
  [theme.breakpoints.up(2560)]: {
    // 2K
    top: "56%",
    right: "27%",
    transform: "translateY(-50%) scale(1.1)",
  },
  [theme.breakpoints.up(3840)]: {
    // 4K
    top: "56%",
    right: "27%",
    transform: "translateY(-50%) scale(1.3)",
  },
}));

const NotchedSquare = styled(Box)(({ theme, isrotated }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "fixed",
  borderRadius: "12px",
  background: "linear-gradient(135deg, #ffffffcf 0%, #f8f9fa 100%)",
  width: "390px",
  height: "390px",
  borderRadius: "50%",
  cursor: "pointer",
  boxShadow: `
  0 0 10px rgba(255, 255, 255, 0.6),
  0 0 30px rgba(255, 255, 255, 0.4),
  0 0 60px rgba(255, 255, 255, 0.2)
`,

  border: "2px solid #2d8659",
  transform: isrotated === "true" ? "rotate(0deg)" : "none",
  // boxShadow: "0 8px 25px rgba(45, 134, 89, 0.2)",

  // Responsive sizing
  [theme.breakpoints.up(2560)]: {
    // 2K
    width: "350px",
    height: "350px",
  },
  [theme.breakpoints.up(3840)]: {
    // 4K
    width: "450px",
    height: "450px",
  },
}));

const RotatedContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  textAlign: "center",
  // padding: "12px",
  background: "transparent",
  transform: "rotate(0deg)",
}));

const LogoImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  filter: "drop-shadow(0 2px 4px rgba(45, 134, 89, 0.2))",

  // Responsive sizing
  [theme.breakpoints.up(2560)]: {
    // 2K
    width: "350px",
    height: "230px",
  },
  [theme.breakpoints.up(3840)]: {
    // 4K
    width: "450px",
    height: "300px",
  },
}));

const CategoryGridContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: "20%",
  left: "5%",
  zIndex: 900,
  width: "350px",

  // Responsive positioning and sizing
  [theme.breakpoints.up(1920)]: {
    // 1K
    width: "420px",
    bottom: "20%",
    left: "3%",
  },
  [theme.breakpoints.up(2560)]: {
    // 2K
    width: "420px",
    bottom: "10%",
    left: "5%",
  },
  [theme.breakpoints.up(3840)]: {
    // 4K
    width: "550px",
    bottom: "10%",
    left: "5%",
  },
}));

const CategoryGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gridTemplateRows: "repeat(3, 1fr)",
  gap: "10px",
  width: "100%",
}));

const CategoryBox = styled(Box)(({ theme, index, isvisible }) => ({
  width: "100%",
  height: "90px",
  background: "linear-gradient(135deg, #000 0%, #2d8659 50%, #000 100%)",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  textShadow: `
    -1px -1px 0 #000,
     1px -1px 0 #000,
    -1px  1px 0 #000,
     1px  1px 0 #000
  `,
  fontWeight: "bold",
  fontSize: "1.4rem",
  textAlign: "center",
  animation:
    isvisible === "true"
      ? `${slideFromTop} 0.6s ease-out ${index * 0.1}s both`
      : "none",
  boxShadow: "0 4px 15px rgba(255, 255, 255, 0.3)",
  cursor: "default",
  padding: "8px",

  // Responsive sizing
  [theme.breakpoints.up(2560)]: {
    // 2K
    height: "160px",
    fontSize: "1.1rem",
  },
  [theme.breakpoints.up(3840)]: {
    // 4K
    height: "200px",
    fontSize: "1.4rem",
    gap: "35px",
  },
}));

const ProjectsGrid = styled(Grid)(({ theme }) => ({
  position: "fixed",
  top: "10%",
  right: "2%",
  width: "70%",
  height: "100%",
  zIndex: 800,
  display: "flex",
  flexDirection: "column",
  gap: "10px",

  [theme.breakpoints.up(1920)]: {
    width: "70%",
    height: "100%",
    top: "10%",
    right: "2%",
  },
  [theme.breakpoints.up(2560)]: {
    width: "70%",
    height: "100%",
    top: "15%",
    right: "2%",
  },
  [theme.breakpoints.up(3840)]: {
    width: "70%",
    height: "100%",
    top: "15%",
    right: "2%",
  },
}));

const ProjectCardWrapper = styled(Card)(({ theme, index, isvisible }) => ({
  borderRadius: "12px",
  overflow: "hidden",
  height: "100%",
  width: "100%",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  backgroundColor: "transparent",
  animation:
    isvisible === "true"
      ? `${slideFromLeft} 0.6s ease-out ${index * 0.1 + 0.8}s both`
      : "none",
  cursor: "pointer",
  transition: "all 0.3s ease",
  border: "2px solid rgba(255, 255, 255, 0.2)",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 12px 40px rgba(255, 255, 255, 0.3)",
    border: "2px solid rgba(255, 255, 255, 0.4)",
  },
}));

const ProjectImage = styled(CardMedia)({
  height: "100%",
  width: "100%",
  objectFit: "cover",
});

const SliderContainer = styled(Box)({
  display: "flex", // <-- make it a row
  gap: "10px", // space between slider and video
  width: "100%", // take full width of Grid item
  overflow: "hidden",
  position: "relative",
  borderRadius: "12px",
  height: "400px", // fixed height for row
  top: "10px",
  border: "2px solid rgba(255, 255, 255, 0.2)",
});

const SliderTrack = styled(Box)({
  display: "flex",
  animation: `${slide} 30s linear infinite`,
  width: "158%", // keep your slider width
});

const SliderItem = styled(Card)({
  flex: "0 0 100%", // show 4 items at a time
  height: "100%", // fill slider height
  borderRadius: "8px",
  overflow: "hidden",
  margin: "0 1px",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 8px 24px rgba(255, 255, 255, 0.2)",
  },
});

const BackButton = styled(Button)(({ theme }) => ({
  position: "fixed",
  top: "20px",
  left: "20px",
  zIndex: 1000,
  color: "white",
  border: "2px solid rgba(255, 255, 255, 0.4)",
  padding: "10px 24px",
  borderRadius: "25px",
  fontSize: "0.95rem",
  cursor: "pointer",
  background: "rgba(15, 23, 42, 0.8)",
  backdropFilter: "blur(15px)",
  boxShadow: "0 8px 25px rgba(45, 134, 89, 0.3)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  fontWeight: 600,
  "&:hover": {
    borderColor: "rgba(255, 255, 255, 0.8)",
    transform: "translateY(-3px)",
    boxShadow: "0 12px 35px rgba(45, 134, 89, 0.5)",
    background: "rgba(15, 23, 42, 0.9)",
  },

  // Responsive sizing
  [theme.breakpoints.up(2560)]: {
    // 2K
    padding: "12px 28px",
    fontSize: "1.1rem",
    top: "25px",
    left: "25px",
  },
  [theme.breakpoints.up(3840)]: {
    // 4K
    padding: "15px 32px",
    fontSize: "1.3rem",
    top: "30px",
    left: "30px",
  },
}));

// Modal Styled Components
const ModalBackdrop = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.9)",
  backdropFilter: "blur(10px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  animation: `${fadeIn} 0.3s ease-out`,
});

const ModalContent = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "80vw",
  height: "80vh",
  margin: "10vh 10vw", // 10% gap from each side
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  animation: `${scaleUp} 0.3s ease-out`,

  // Responsive modal sizing
  [theme.breakpoints.up(1920)]: {
    // 1K
    width: "80vw",
    height: "80vh",
    margin: "10vh 10vw",
  },
  [theme.breakpoints.up(2560)]: {
    // 2K
    width: "80vw",
    height: "80vh",
    margin: "10vh 10vw",
  },
  [theme.breakpoints.up(3840)]: {
    // 4K
    width: "80vw",
    height: "80vh",
    margin: "10vh 10vw",
  },
}));

const ModalImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  borderRadius: "12px",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.8)",
});

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "-50px",
  right: "0",
  color: "white",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  border: "2px solid rgba(255, 255, 255, 0.4)",
  padding: "8px",
  zIndex: 2001,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderColor: "rgba(255, 255, 255, 0.8)",
  },

  // Responsive close button
  [theme.breakpoints.up(2560)]: {
    // 2K
    padding: "10px",
    top: "-60px",
    right: "0",
    "& .MuiSvgIcon-root": {
      fontSize: "2rem",
    },
  },
  [theme.breakpoints.up(3840)]: {
    // 4K
    padding: "12px",
    top: "-70px",
    right: "0",
    "& .MuiSvgIcon-root": {
      fontSize: "2.5rem",
    },
  },
}));

// Projects Data
const projects = [
  {
    id: 1,
    image: "/images/pcap1.jpg",
  },
  {
    id: 2,
    image: "/images/pcap2.jpg",
  },
  {
    id: 3,
    image: "/images/pcap3.jpg",
  },
  {
    id: 4,
    image: "/images/pcap4.jpg",
  },
];

const categories = [
  "600+ Electric Buses",
  "120,000 Electric Two/Three Wheeler",
  "Buyback of 900+ Polluting Vehicles",
  "Installation of 25+ AQMS",
  "Vehicle Inspection System",
  "Provision of 5000+ Super Seeders",
];

const sliderImages = Array.from({ length: 13 }, (_, index) => ({
  id: `slider${index + 1}`,
  image: `images/SliderPcap/${index + 1}.png`,
}));

const sliderImagesForLoop = [...sliderImages, ...sliderImages];

const EMobilityPage = () => {
  const navigate = useNavigate();
  const [, setIsVideoPlaying] = useState(false);
  const [animationStage, setAnimationStage] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Start animation sequence immediately when component mounts
    const timer1 = setTimeout(() => {
      setAnimationStage(1); // Start button animation to top-left
    }, 100);

    const timer2 = setTimeout(() => {
      setAnimationStage(2); // Show category boxes
    }, 1600);

    const timer3 = setTimeout(() => {
      setAnimationStage(3); // Show projects grid
    }, 2200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
  };

  const handleImageClick = (project) => {
    setSelectedImage(project.image);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <PageWrapper>
      <BackButton startIcon={<ArrowBack />} onClick={() => navigate("/")}>
        Back
      </BackButton>

      <AnimatedButton
        isanimating={(animationStage >= 1).toString()}
        isvisible={(animationStage <= 1).toString()}
      >
        <NotchedSquare isrotated="true">
          <RotatedContent>
            <LogoImage src="/images/pcap.png" alt="pcap" />
          </RotatedContent>
        </NotchedSquare>
      </AnimatedButton>

      {/* Category Grid - Bottom Left Side in 2x3 layout */}
      {animationStage >= 2 && (
        <CategoryGridContainer>
          <CategoryGrid>
            {categories.map((category, index) => (
              <CategoryBox
                key={category}
                index={index}
                isvisible={(animationStage >= 2).toString()}
              >
                {category}
              </CategoryBox>
            ))}
          </CategoryGrid>
        </CategoryGridContainer>
      )}

      {/* Projects Grid - Right 70% Side - Fixed position */}
      {animationStage >= 3 && (
        <ProjectsGrid container>
          {/* Top Row - 4 Static Images */}
          <Grid
            item
            xs={12}
            sx={{
              height: {
                lg: "350px",
                xl: "400px",
                "2k": "500px",
                "4k": "700px",
              },
            }}
          >
            <Grid container spacing={2} sx={{ height: "100%" }}>
              {projects.map((project, index) => (
                <Grid
                  item
                  xs={3}
                  key={project.id}
                  sx={{ height: "100%", padding: "8px" }}
                >
                  <ProjectCardWrapper
                    index={index}
                    isvisible={(animationStage >= 3).toString()}
                    onClick={() => handleImageClick(project)}
                  >
                    <ProjectImage
                      component="img"
                      alt={`E-mobility project ${project.id}`}
                      image={project.image}
                    />
                  </ProjectCardWrapper>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Second Row - Auto-moving Slider */}

          <Grid item xs={12}>
            <SliderContainer>
              {/* Video takes 40% */}
              <Box sx={{ width: "60%", height: "100%" }}>
                <video
                  controls
                  autoPlay
                  loop
                  src="videos/PCAP.mp4"
                  preload="metadata"
                  onEnded={handleVideoEnd}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>

              {/* Slider takes 60% */}
              <Box sx={{ width: "40%", height: "100%", overflow: "hidden" }}>
                <SliderTrack>
                  {sliderImagesForLoop.map((project, index) => (
                    <SliderItem key={`slider-${project.id}-${index}`}>
                      <CardMedia
                        component="img"
                        alt={`Slider image ${project.id}`}
                        image={project.image}
                        sx={{
                          height: "100%",
                          width: "100%",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.src = "/images/placeholder.jpg";
                        }}
                      />
                    </SliderItem>
                  ))}
                </SliderTrack>
              </Box>
            </SliderContainer>
          </Grid>
        </ProjectsGrid>
      )}

      {/* Modal for full-size image */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="image-modal"
        aria-describedby="full-size-image"
      >
        <ModalBackdrop onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>
              <Close />
            </CloseButton>
            {selectedImage && (
              <ModalImage src={selectedImage} alt="Full size project image" />
            )}
          </ModalContent>
        </ModalBackdrop>
      </Modal>
    </PageWrapper>
  );
};

export default EMobilityPage;
